import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { checklistQueriesToScreenerTokens } from '~/utilities/checklistQueryParse';
import {
  getInvestingStyleScreenerQueries,
  hasInvestingStyleScreenerQueries,
} from '../data/investingStyleScreenerQueries';
import { INVESTING_STYLE_IDS, type InvestingStyleId } from '../types';

export interface InvestingStyleCountState {
  total: number | null;
  isLoading: boolean;
  error: boolean;
}

function createEmptyState(): InvestingStyleCountState {
  return { total: null, isLoading: false, error: false };
}

const ALL_STYLES_WITH_QUERIES = INVESTING_STYLE_IDS.filter(hasInvestingStyleScreenerQueries);

export function useOnboardingInvestingStyleCounts(isActive: boolean) {
  const [countsByStyleId, setCountsByStyleId] = useState<
    Record<string, InvestingStyleCountState>
  >({});
  const cacheRef = useRef<Record<string, InvestingStyleCountState>>({});

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const abortController = new AbortController();

    const nextState: Record<string, InvestingStyleCountState> = {};
    for (const styleId of ALL_STYLES_WITH_QUERIES) {
      const cached = cacheRef.current[styleId];
      nextState[styleId] = cached ?? {
        ...createEmptyState(),
        isLoading: true,
      };
    }
    cacheRef.current = nextState;
    setCountsByStyleId(nextState);

    const fetchCount = async (styleId: InvestingStyleId) => {
      const queries = getInvestingStyleScreenerQueries(styleId);
      if (queries.length === 0) {
        return;
      }

      const cached = cacheRef.current[styleId];
      if (cached?.total != null && !cached.error && !cached.isLoading) {
        return;
      }

      cacheRef.current[styleId] = {
        total: cached?.total ?? null,
        isLoading: true,
        error: false,
      };
      setCountsByStyleId({ ...cacheRef.current });

      try {
        const tokens = checklistQueriesToScreenerTokens(queries);
        if (tokens.length === 0) {
          throw new Error('Invalid screener query');
        }

        const response = await axios.post<{ total?: number }>(
          '/api/company-screener',
          {
            data: tokens,
            limit: 1,
            offset: 0,
            sort_by: 'market_cap',
            sort_direction: 'desc',
          },
          { signal: abortController.signal },
        );

        if (abortController.signal.aborted) {
          return;
        }

        const total = Number(response.data?.total ?? 0);
        cacheRef.current[styleId] = {
          total: Number.isFinite(total) ? total : 0,
          isLoading: false,
          error: false,
        };
        setCountsByStyleId({ ...cacheRef.current });
      } catch (err: unknown) {
        if (abortController.signal.aborted || axios.isCancel(err)) {
          return;
        }

        cacheRef.current[styleId] = {
          total: null,
          isLoading: false,
          error: true,
        };
        setCountsByStyleId({ ...cacheRef.current });
      }
    };

    for (const styleId of ALL_STYLES_WITH_QUERIES) {
      const cached = cacheRef.current[styleId];
      if (cached?.total != null && !cached.error && !cached.isLoading) {
        continue;
      }
      void fetchCount(styleId);
    }

    return () => abortController.abort();
  }, [isActive]);

  return countsByStyleId;
}

/** Sum of per-style screener totals for the current selection (OR / additive). */
export function sumInvestingStyleCounts(
  styleIds: InvestingStyleId[],
  countsByStyleId: Record<string, InvestingStyleCountState>,
): { total: number; isLoading: boolean; error: boolean } {
  if (styleIds.length === 0) {
    return { total: 0, isLoading: false, error: false };
  }

  let total = 0;
  let isLoading = false;
  let error = false;

  for (const styleId of styleIds) {
    const state = countsByStyleId[styleId];
    if (!state || state.isLoading) {
      isLoading = true;
      continue;
    }
    if (state.error || state.total == null) {
      error = true;
      continue;
    }
    total += state.total;
  }

  return { total, isLoading, error };
}
